/**
 * Clientes — lista y plazas, `TDS-CORE` Parte 1 §4, §6.4 y §7; `URS §64`.
 *
 * Seis estados de `OD-0017 D-03`: cargando, vacío (con la siguiente acción,
 * `OD-0015 D-06`), con datos, error, sin permiso y límite alcanzado (con lo que
 * se puede hacer, `URS §110`). El contador de plazas se ve siempre: no se
 * descubre el límite al chocar con él.
 *
 * El buscador filtra la lista que ya está cargada (TODOS los clientes, página a
 * página: `api.listarClientes`), sin tildes y por palabras. Lo pidió el
 * propietario tras probar en staging (2026-10-25): con 153 clientes, la lista
 * solo se recorría bajando, y a los de la «E» en adelante se llegaba solo desde
 * el buscador global.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type Cliente, type Plazas } from "../api/cliente";
import { t } from "../i18n/es";
import { coincide } from "../ui/coincide";
import { Icono } from "../ui/Iconos";
import { Aviso, Boton, Campo, FilaPulsable, Tarjeta } from "../ui/primitivas";

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "datos"; readonly clientes: readonly Cliente[]; readonly plazas: Plazas | null };

export function PantallaClientes({
  onAbrir,
  onNuevo,
  onSesionCaducada,
}: {
  onAbrir: (id: string) => void;
  onNuevo: () => void;
  onSesionCaducada: () => void;
}) {
  const [filtro, setFiltro] = useState<"ACTIVE" | "ARCHIVED">("ACTIVE");
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  const [busqueda, setBusqueda] = useState("");

  const cargar = useCallback(async (): Promise<void> => {
    setEstado({ tipo: "cargando" });
    const [lista, plazas] = await Promise.all([api.listarClientes(filtro), api.plazasClientes()]);
    if (!lista.ok) {
      if (lista.estado === 401) onSesionCaducada();
      else setEstado({ tipo: lista.estado === 403 ? "sin-permiso" : "error" });
      return;
    }
    setEstado({ tipo: "datos", clientes: lista.datos, plazas: plazas.ok ? plazas.datos : null });
  }, [filtro, onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const plazas = estado.tipo === "datos" ? estado.plazas : null;
  const todos = estado.tipo === "datos" ? estado.clientes : [];
  const visibles = todos.filter((c) => coincide(busqueda, c.displayName, c.legalName, c.taxIdNormalized, c.email, c.city));
  const sinResultados = todos.length > 0 && visibles.length === 0;
  const lleno = plazas !== null && plazas.available === 0;

  return (
    <Tarjeta titulo={t.clientes.titulo}>
      {plazas !== null ? (
        <div className="progreso">
          {plazas.total !== null ? (
            <progress value={plazas.used} max={plazas.total} aria-labelledby="plazas-texto" />
          ) : null}
          <p id="plazas-texto" className="ayuda-neutra">
            {plazas.total === null ? t.clientes.plazasSinLimite(plazas.used) : t.clientes.plazas(plazas.used, plazas.total)}
          </p>
        </div>
      ) : null}
      {lleno && plazas !== null ? <Aviso tono="aviso">{t.clientes.plazasLlenas(plazas.releasable)}</Aviso> : null}

      <div className="barra-acciones">
        <div className="segmentado" role="group" aria-label={t.clientes.filtro}>
          {(["ACTIVE", "ARCHIVED"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className="menu-opcion"
              aria-pressed={filtro === f}
              onClick={() => setFiltro(f)}
            >
              {f === "ACTIVE" ? t.clientes.activos : t.clientes.archivados}
            </button>
          ))}
        </div>
        <Boton onClick={onNuevo} deshabilitado={lleno || estado.tipo !== "datos"}>
          {t.clientes.nuevo}
        </Boton>
      </div>

      {estado.tipo === "cargando" ? <p>{t.clientes.cargando}</p> : null}
      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.clientes.sinPermiso}</Aviso> : null}
      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.clientes.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.clientes.reintentar}
          </Boton>
        </>
      ) : null}

      {/* Sin clientes activos, el mismo estado vacio que Productos y Facturas
          en vez de una franja con un aviso (auditoria de diseno, 2026-10-19).
          El boton de crear ya esta en la barra de arriba. */}
      {estado.tipo === "datos" && estado.clientes.length === 0 && filtro === "ACTIVE" ? (
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="clientes" />
          </span>
          <p>{t.clientes.vacioActivos}</p>
        </div>
      ) : null}
      {estado.tipo === "datos" && estado.clientes.length === 0 && filtro === "ARCHIVED" ? (
        <Aviso tono="info">{t.clientes.vacioArchivados}</Aviso>
      ) : null}

      {todos.length > 0 ? (
        <>
          <Campo id="cli-buscar" etiqueta={t.clientes.buscar} valor={busqueda} onChange={setBusqueda} autoComplete="off" requerido={false} />
          <p className="ayuda-neutra" aria-live="polite">
            {t.clientes.cuantos(visibles.length, todos.length)}
          </p>
        </>
      ) : null}
      {sinResultados ? <Aviso tono="info">{t.clientes.sinResultados}</Aviso> : null}

      {visibles.length > 0 ? (
        <ul className="lista-filas">
          {visibles.map((c) => (
            <FilaPulsable key={c.id} onClick={() => onAbrir(c.id)}>
              <span className="fila-titulo">{c.displayName}</span>
              <span className="ayuda-neutra">
                {c.taxIdNormalized ?? t.clientes.sinNif}
                {c.email !== null ? ` · ${c.email}` : ""}
              </span>
            </FilaPulsable>
          ))}
        </ul>
      ) : null}
    </Tarjeta>
  );
}
